import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import PageLayout from 'layouts/PageLayout';
import SearchFilter from 'components/SearchFilter';
import ModalBusyContainer from 'components/ModalBusyContainer';
import withTableProps from 'HOC/types/withTable';
import withTable from 'HOC/withTable';
import { routesAliases } from 'router/routesAliases';
import { actions as modalActions } from 'redux/modal';
import { exportJobsTasks, getJobsTasks, deleteJobTask } from 'http/jobsTasks';
import { transformTableData, getConfirmationModalConfig, resolveApiErrorMessage } from 'utils';
import { useAsyncFn } from 'react-use';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ROLES } from 'appConstants';
import getTableConfig from './helpers/getTableConfig';

const pageAlias = 'JobTasksList';

const JobsTasks = ({ tableData }) => {
  const dispatch = useDispatch();
  const tableConfig = getTableConfig();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const userRole = useSelector(state => state.auth.profile?.role);

  const [columns, jobsTasks] = useMemo(
    () => transformTableData(tableConfig, tableData.data),
    [tableConfig, tableData.data],
  );

  const handleCloseModal = () => dispatch(modalActions.closeModal());

  const [stateDeleteJobTask, deleteTask] = useAsyncFn(async id => {
    const data = await deleteJobTask(id);
    const isSuccess = !data.error && !data.statusCode;

    if (isSuccess) {
      tableData.handleForceUpdate();
      setTimeout(() => {
        handleCloseModal();
      }, 100);
    } else {
      const errorMessage = resolveApiErrorMessage(data.message, {
        t,
        fallbackMessage: t('jobsTasks.cantDelete'),
      });
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: handleCloseModal,
        rejectAction: handleCloseModal,
        message: errorMessage,
      });
      dispatch(modalActions.openModal(config, element));
    }
  }, []);

  const handleDeleteJobTask = id => {
    const [config, element] = getConfirmationModalConfig({
      title: t('jobsTasks.deleteTaskModalTitle'),
      confirmAction: () => deleteTask(id),
      rejectAction: handleCloseModal,
      message: t('jobsTasks.deleteTaskConfirmationMessage'),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const handleTableSearch = (value, searchAlias) => {
    tableData.handleFiltering(searchAlias, value);
  };

  const handleTableSearchClearing = searchAlias => {
    tableData.handleRemoveFilter(searchAlias);
  };

  useEffect(() => {
    tableData.handleChangeFetchMethod(pageAlias, getJobsTasks);
  }, []);

  // Update table when returning from edit/create page
  useEffect(() => {
    // Check if we're returning from edit or create page
    if (location.pathname === routesAliases.jobsTasksList) {
      // Force refresh table data when returning to list page
      tableData.handleForceUpdate();
    }
  }, [location.pathname]);

  const isTableLoading = tableData.isFetchingTableData || stateDeleteJobTask.loading;

  // Filter actions based on user role and job task type (system vs company)
  const tableActions = useMemo(() => {
    const isSystemCopy = rowData => rowData.isFromSystem === true;

    const actions = [
      {
        type: 'icon',
        icon: 'edit',
        getIcon: rowData => (userRole === ROLES.SUPER && isSystemCopy(rowData) ? 'visibility' : 'edit'),
        onClick: event => {
          history.push({
            pathname: `${routesAliases.editJobTask}/${event.currentTarget.dataset.key}`,
          });
        },
        getDisabled: rowData => {
          const companyId = rowData.companyId;
          if (userRole === ROLES.HYPER && companyId !== 0 && companyId !== undefined && companyId !== null) {
            return true;
          }
          return false;
        },
      },
      {
        type: 'icon',
        icon: 'delete',
        color: 'error',
        onClick: event => {
          handleDeleteJobTask(event.currentTarget.dataset.key);
        },
        getHidden: rowData => userRole === ROLES.SUPER && isSystemCopy(rowData),
        getDisabled: rowData => {
          const companyId = rowData.companyId;
          if (userRole === ROLES.HYPER && companyId !== 0 && companyId !== undefined && companyId !== null) {
            return true;
          }
          return false;
        },
      },
    ];

    return actions;
  }, [userRole, history, handleDeleteJobTask]);

  const getRowStyle = rowData => {
    if (userRole === ROLES.SUPER && rowData.isFromSystem === true) {
      return { backgroundColor: '#f5f5f5' };
    }
    if (userRole === ROLES.HYPER && rowData.companyId === 0) {
      return { backgroundColor: '#f5f5f5' };
    }
    return {};
  };

  return (
    <PageLayout
      pageTitle={t('jobsTasks.pageTitle')}
      actionSectionLeft={[
        {
          type: 'component',
          Component: (
            <SearchFilter searchAlias="search" onSearch={handleTableSearch} onClearSearch={handleTableSearchClearing} />
          ),
        },
      ]}
      actionSectionRight={[
        {
          type: 'button',
          icon: 'add',
          title: t('jobsTasks.actionAdd'),
          onClick: () => {
            history.push(`${routesAliases.createJobTask}`);
          },
        },
        {
          type: 'button',
          icon: 'import_export',
          title: t('commonActions.actionExport'),
          onClick: exportJobsTasks,
        },
      ]}
    >
      <ModalBusyContainer busy={isTableLoading}>
        <AppTable
          columns={columns}
          data={jobsTasks}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
          currentPage={tableData.page}
          isLazyLoading
          actions={tableActions}
          getRowStyle={getRowStyle}
        />
      </ModalBusyContainer>
    </PageLayout>
  );
};

JobsTasks.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable({
  defaultSorting: {
    field: 'id',
    dir: 'desc',
  },
  fetchingData: 'items',
  pageAlias,
})(JobsTasks);
